import api from '@/services/api'
import { player, usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { computed, onMounted, onUnmounted, readonly, ref } from 'vue'
import { ACTIVITY_ID } from '@/types/shared/player'
import { useNow } from '@/composables/game/time'
import { thisOccurrence } from '@/helpers/date'
import { useAnalytics } from '@/composables/analytics'
import { useConfig } from '@/composables/game/config'
import webapp from '@/services/webapp'
import { adControllers } from '@/services/ad-controller'
import { env } from '@/env'
import { DateTime } from 'luxon'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import type { Sonar } from '@/types/sonar'

export enum AD_TYPE {
  FOR_SPIN,
  FOR_DOUBLE_PRIZE,
  FOR_OFFLINE_PROFIT,

}

const adTypeMapper = {
  [AD_TYPE.FOR_SPIN]: ACTIVITY_ID.WATCH_AD_FOR_SPIN,
  [AD_TYPE.FOR_DOUBLE_PRIZE]: ACTIVITY_ID.WATCH_AD_FOR_DOUBLE_PRIZE,
  [AD_TYPE.FOR_OFFLINE_PROFIT]: ACTIVITY_ID.WATCH_AD_FOR_DOUBLE_OFFLINE_PROFIT,
}

const lastAdClaimTimestamp = useLocalStorage(buildDropeeStorageKey('lastAdClaimTimestamp'), 0)
enum AD_PROVIDER {
  MONETAG,
  AD_SONAR,
  ADS_GRAM,
}
function getProvider(): AD_PROVIDER {
  if (env.app.isProd) {
    return AD_PROVIDER.MONETAG
  } else {
    return AD_PROVIDER.ADS_GRAM
  }
}
export const useAd = (adType = AD_TYPE.FOR_SPIN) => {
  const provider = getProvider()
  const { updatePlayer } = usePlayer()
  const { updateState } = useFortuneWheel()
  const { nowDateTimestamp, now } = useNow()
  const { triggerEvent } = useAnalytics()
  const { config } = useConfig()
  const activityId = adTypeMapper[adType]
  const adController = {
    [AD_TYPE.FOR_SPIN]: adControllers.adForSpin,
    [AD_TYPE.FOR_DOUBLE_PRIZE]: adControllers.adForDoublePrize,
    [AD_TYPE.FOR_OFFLINE_PROFIT]: adControllers.adForDoubleOfflineProfit,
  }[adType]
  const disabledSecondsBetweenAds = 10
  const countdownTimeBetweenAds = computed(() => {
    if (provider !== AD_PROVIDER.AD_SONAR) return 0
    const remaining = disabledSecondsBetweenAds - (now.value - lastAdClaimTimestamp.value)
    return Math.max(0, remaining)
  })
  const showAdsCountdown = computed(() => countdownTimeBetweenAds.value > 0)

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())

  async function noAdHandler() {
    await webapp.showAlert('Oops! No ads available at the moment. Try again later 🙏')
  }

  onMounted(() => {
    adController.addEventListener('onBannerNotFound', noAdHandler)
  })

  onUnmounted(() => {
    adController.removeEventListener('onBannerNotFound', noAdHandler)
  })

  async function claimAdReward() {
    if (adType === AD_TYPE.FOR_SPIN) {
      const response = await api.getSpinForAd()
      updatePlayer(response.playerStats)
      await updateState(response.fortuneWheelState)
    } else if (adType === AD_TYPE.FOR_OFFLINE_PROFIT) {
      const response = await api.multiplyOfflineProfitForAd()
      updatePlayer(response.playerStats)
    } else {
      const response = await api.getDoublePrizeForAd({ isAutoSpin: false })
      updatePlayer(response.playerStats)
    }
    lastAdClaimTimestamp.value = Math.round(DateTime.now().toSeconds())
  }

  async function showAd() {
    if (provider === AD_PROVIDER.MONETAG) {
      return await showMonetag()
    } else if (provider === AD_PROVIDER.AD_SONAR) {
      return await showAdSonar()
    } else {
      return await showAdsGram()
    }
  }

  async function showAdsGram() {
    const reward = adType === AD_TYPE.FOR_SPIN ? 'spin' : 'double_prize'
    triggerEvent({ event: 'start_ad', metadata: { reward } })
    let adWasShown = !env.app.isProd

    function onStart() {
      adWasShown = true
    }

    adController.addEventListener('onStart', onStart)
    const result = await adController.show()
    adController.removeEventListener('onStart', onStart)
    if (result.done && adWasShown) {
      triggerEvent({ event: 'watch_ad', metadata: { reward } })
      await claimAdReward()
      return true
    }
    triggerEvent({ event: 'cancel_ad', metadata: { reward } })
    return false
  }

  async function showAdSonar() {
    const reward = adType === AD_TYPE.FOR_SPIN ? 'spin' : 'double_prize'
    triggerEvent({ event: 'start_ad', metadata: { reward } })

    const result = await _showAdSonar()
    if (result) {
      triggerEvent({ event: 'watch_ad', metadata: { reward } })
      await claimAdReward()
      return true
    }
    triggerEvent({ event: 'cancel_ad', metadata: { reward } })
    return false
  }

  async function showMonetag() {
    const reward = adType === AD_TYPE.FOR_SPIN ? 'spin' : 'double_prize'
    triggerEvent({ event: 'start_ad', metadata: { reward } })
    const showMonetagAd = (window as unknown as { show_9777660: () => Promise<void> }).show_9777660

    try {
      await showMonetagAd()

      triggerEvent({ event: 'watch_ad', metadata: { reward } })
      await claimAdReward()
      return true
    } catch {
      triggerEvent({ event: 'cancel_ad', metadata: { reward } })
      return false
    }
  }

  function _showAdSonar(): Promise<boolean> {
    const adSonar = (window as unknown as { Sonar: Sonar }).Sonar
    let isResolved = false
    return new Promise((resolve) => {
      adSonar.show({
        adUnit: 'adsonar_all',
        onReward() {
          isResolved = true
          resolve(true)
        },
        onError: () => {
          isResolved = true
          resolve(false)
        },
        onClose: () => {
          isResolved = true
          resolve(false)
        },
      }).catch(() => {
        isResolved = true
        resolve(false)
      })
    })
  }

  const maxDailyAdsCounts: Record<AD_TYPE, number> = {
    [AD_TYPE.FOR_SPIN]: config.value.game.fortuneWheel.extraSpin.watchAd.maxPerDay,
    [AD_TYPE.FOR_DOUBLE_PRIZE]: config.value.game.fortuneWheel.watchAdForDoublePrize.maxPerDay,
    [AD_TYPE.FOR_OFFLINE_PROFIT]: config.value.game.ads.doubleOfflineProfit.maxPerDay,
  }
  const maxDailyAdsCount = readonly(ref(maxDailyAdsCounts[adType]))

  const watchedDailyAdsCount = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return 0
    return Math.min(player.value.activities[activityId], maxDailyAdsCount.value)
  })

  const claimed = computed(() => {
    if (!player.value.activities) {
      return false
    }
    return player.value.activities.date === todayDate.value && player.value.activities[activityId] >= maxDailyAdsCount.value
  })

  const adWatchedSpinReward = computed(() => config.value.game.fortuneWheel.extraSpin.watchAd.amount)

  const adWatchedOfflineProfitMultiplier = computed(() => config.value.game.ads.doubleOfflineProfit.multiplier)

  return {
    showAd: showAd,
    watchedDailyAdsCount,
    claimed,
    maxDailyAdsCount,
    adWatchedSpinReward,
    adWatchedOfflineProfitMultiplier,
    countdownTimeBetweenAds,
    showAdsCountdown,
  }
}
